import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import toast from 'react-hot-toast';
import {
  CheckCircle, AlertTriangle, ChevronDown, ChevronUp, Clock,
  Play, Square, MessageSquare, Package, X,
  CheckSquare, Upload, RefreshCw, Camera
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM yyyy') : '-'; } catch { return '-'; } };
const today = () => new Date().toISOString().split('T')[0];
const minsToHrs = m => { const h = Math.floor(m / 60); const min = m % 60; return h > 0 ? `${h}h ${min}m` : `${min}m`; };

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400', border: 'border-yellow-300' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500',   border: 'border-blue-300'   },
  completed:   { label: 'Completed',   bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  border: 'border-green-300'  },
  waiting:     { label: 'Waiting',     bg: 'bg-gray-100',   text: 'text-gray-400',   dot: 'bg-gray-300',   border: 'border-gray-200'   },
  on_hold:     { label: 'On Hold',     bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-400', border: 'border-orange-300' },
  delayed:     { label: 'Delayed',     bg: 'bg-red-100',    text: 'text-red-600',    dot: 'bg-red-500',    border: 'border-red-300'    },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ── Time Tracker ──────────────────────────────────────────────────────────────
function TimeTracker({ task, onSessionChange }) {
  const [session, setSession] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [customDateTime, setCustomDateTime] = useState('');
  const timerRef = useRef(null);

  // Default to current datetime for picker
  const getNowLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  useEffect(() => {
    api.get(`/tasks/${task.id}/active-session`).then(r => {
      if (r.data.active) { setSession(r.data.active); startTimer(new Date(r.data.active.clock_in)); }
      if (onSessionChange) onSessionChange(r.data.active || null);
    }).catch(() => {});
    return () => clearInterval(timerRef.current);
  }, [task.id]);

  const startTimer = (fromTime) => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - new Date(fromTime).getTime()) / 1000)), 1000);
  };

  const clockIn = async (customTime) => {
    setLoading(true);
    try {
      const payload = customTime ? { clock_in: new Date(customTime).toISOString() } : {};
      const r = await api.post(`/tasks/${task.id}/clock-in`, payload);
      const clockInTime = customTime ? new Date(customTime) : new Date();
      setSession({ id: r.data.log_id, clock_in: clockInTime.toISOString() });
      startTimer(clockInTime);
      if (onSessionChange) onSessionChange({ id: r.data.log_id });
      setShowPicker(false);
      toast.success('Clocked in! ⏱');
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const clockOut = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/tasks/${task.id}/clock-out`, {});
      clearInterval(timerRef.current);
      setSession(null); setElapsed(0);
      if (onSessionChange) onSessionChange(null);
      toast.success(`${minsToHrs(r.data.duration_minutes)} worked ✅`);
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const elapsedStr = () => {
    const h = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };

  if (session) return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-mono text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg animate-pulse">⏱ {elapsedStr()}</span>
      <button onClick={clockOut} disabled={loading} className="text-xs bg-red-500 hover:bg-red-600 text-white px-2.5 py-1 rounded-lg flex items-center gap-1 font-medium disabled:opacity-50">
        <Square className="w-3 h-3" /> Out
      </button>
    </div>
  );

  if (showPicker) return (
    <div className="flex flex-col gap-2 bg-green-50 border border-green-200 rounded-xl p-2.5">
      <p className="text-xs font-semibold text-green-700">⏱ When did you clock in?</p>
      <input
        type="datetime-local"
        value={customDateTime || getNowLocal()}
        onChange={e => setCustomDateTime(e.target.value)}
        max={getNowLocal()}
        className="w-full border border-green-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
      />
      <div className="flex gap-1.5">
        <button onClick={() => setShowPicker(false)}
          className="flex-1 text-xs border border-gray-200 text-gray-500 py-1.5 rounded-lg">
          Cancel
        </button>
        <button onClick={() => clockIn(customDateTime || getNowLocal())} disabled={loading}
          className="flex-1 text-xs bg-green-500 hover:bg-green-600 text-white py-1.5 rounded-lg font-semibold disabled:opacity-50">
          {loading ? '...' : '✅ Clock In'}
        </button>
      </div>
    </div>
  );

  return (
    <button onClick={() => { setCustomDateTime(getNowLocal()); setShowPicker(true); }}
      disabled={loading}
      className="text-xs border border-green-300 text-green-700 hover:bg-green-50 px-2.5 py-1 rounded-lg flex items-center gap-1 font-medium disabled:opacity-50">
      <Play className="w-3 h-3" /> Clock In
    </button>
  );
}

// ── Reference Images (Admin uploaded product images) ─────────────────────────
function ReferenceImages({ task }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!task.project_item_id) { setLoading(false); return; }
    api.get(`/projects/${task.project_id}/items/${task.project_item_id}/images`)
      .then(r => setImages(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [task.project_item_id, task.project_id]);

  if (loading) return (
    <div className="flex justify-center py-8">
      <div className="animate-spin w-6 h-6 border-3 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );

  if (images.length === 0) return (
    <div className="text-center py-10 text-gray-400">
      <span className="text-3xl block mb-2">🖼️</span>
      <p className="text-sm font-medium">No reference images available</p>
      <p className="text-xs mt-1">Admin has not uploaded any design/drawing yet</p>
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500 font-medium">Reference images uploaded by admin — use these as guides</p>
      <div className="grid grid-cols-2 gap-2">
        {images.map(img => (
          <div key={img.id} className="relative cursor-pointer" onClick={() => setLightbox(img)}>
            <img src={img.image_url} alt="reference"
              className="w-full h-36 object-cover rounded-xl border-2 border-gray-100 hover:border-amber-400 transition-all"
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        ))}
      </div>
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-sm w-full">
            <img src={lightbox.image_url} alt="ref" className="w-full rounded-2xl shadow-2xl" />
            <button onClick={() => setLightbox(null)} className="absolute top-2 right-2 bg-white/20 text-white rounded-full p-1.5">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Image Section ─────────────────────────────────────────────────────────────
function ImageSection({ task, onUploaded }) {
  const [taskImages, setTaskImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/tasks/${task.id}/images`).then(r => setTaskImages(r.data)).catch(() => {});
  }, [task.id]);

  const reload = () => api.get(`/tasks/${task.id}/images`).then(r => setTaskImages(r.data)).catch(() => {});

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const form = new FormData();
    files.forEach(f => form.append('images', f));
    form.append('image_type', 'progress');
    try {
      await api.post(`/tasks/${task.id}/images`, form);
      toast.success(`${files.length} photo uploaded! ✅`);
      reload();
      if (onUploaded) onUploaded();
    } catch { toast.error('Upload fail'); }
    finally { setUploading(false); fileRef.current.value = ''; }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={() => fileRef.current.click()} disabled={uploading}
          className="flex items-center gap-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl font-medium transition-colors disabled:opacity-50 w-full justify-center">
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : '📷 Upload Photo'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
      </div>
      {taskImages.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {taskImages.map(img => (
            <div key={img.id} className="group cursor-pointer" onClick={() => setLightbox(img)}>
              <img src={buildImageUrl(img.image_url || img.image_path)} alt="task"
                className="w-full h-24 object-cover rounded-xl border border-gray-200 group-hover:ring-2 group-hover:ring-blue-400 transition-all" />
              <p className="text-xs text-gray-400 mt-1 truncate text-center">{img.uploaded_by_name}</p>
            </div>
          ))}
        </div>
      )}
      {taskImages.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-4 bg-gray-50 rounded-xl">No photos yet — upload from above</p>
      )}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <img src={buildImageUrl(lightbox.image_path)} alt="full" className="w-full rounded-2xl shadow-2xl" />
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-2xl px-3 py-2 text-white text-xs">
              📸 {lightbox.uploaded_by_name} — {safeDate(lightbox.created_at)}
            </div>
            <button onClick={() => setLightbox(null)} className="absolute top-2 right-2 bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Task Detail Modal ─────────────────────────────────────────────────────────
function TaskDetailModal({ task, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('progress');
  const [qty, setQty] = useState(task.quantity_completed || 0);
  const [status, setStatus] = useState(task.status === 'waiting' ? 'pending' : task.status);
  const [notes, setNotes] = useState(task.worker_notes || '');
  const [saving, setSaving] = useState(false);
  const [constraint, setConstraint] = useState(null);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryType, setQueryType] = useState('issue');
  const [queryTitle, setQueryTitle] = useState('');
  const [queryDesc, setQueryDesc] = useState('');
  const [queryPriority, setQueryPriority] = useState('medium');
  const [savingQuery, setSavingQuery] = useState(false);
  const [activeSession, setActiveSession] = useState(null);

  const isDelayed = task.status !== 'completed' && task.due_date && task.due_date < today();
  const isDone = task.status === 'completed';
  const isWaiting = task.status === 'waiting';
  const pct = task.quantity_assigned > 0 ? Math.round((task.quantity_completed / task.quantity_assigned) * 100) : 0;

  useEffect(() => {
    if (task.stage_order > 1) {
      api.get(`/tasks/${task.id}/stage-constraint`).then(r => setConstraint(r.data)).catch(() => {});
    }
    // Check if worker is currently clocked in
    api.get(`/tasks/${task.id}/active-session`).then(r => setActiveSession(r.data.active || null)).catch(() => {});
  }, [task.id, task.stage_order]);

  const maxQty = constraint?.constraint_qty >= 0
    ? Math.min(task.quantity_assigned, constraint.constraint_qty)
    : task.quantity_assigned;

  const autoClockOut = async () => {
    if (!activeSession) return true; // No active session, proceed
    try {
      const r = await api.post(`/tasks/${task.id}/clock-out`, {});
      setActiveSession(null);
      toast.success(`⏱ Auto clocked out: ${minsToHrs(r.data.duration_minutes)} worked`);
      return true;
    } catch {
      toast.error('Clock-out failed — please try again');
      return false;
    }
  };

  const handleSave = async () => {
    if (qty > maxQty) return toast.error(`Max ${maxQty} units allowed`);
    const clockedOut = await autoClockOut();
    if (!clockedOut) return;
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}/progress`, {
        quantity_completed: Number(qty),
        status,
        worker_notes: notes
      });
      toast.success('Progress saved! ✅');
      onUpdate();
      onClose();
    } catch (err) {
      const d = err?.response?.data;
      toast.error(d?.message || 'Error');
    }
    finally { setSaving(false); }
  };

  const quickComplete = async () => {
    const clockedOut = await autoClockOut();
    if (!clockedOut) return;
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}/progress`, { quantity_completed: task.quantity_assigned, status: 'completed' });
      toast.success('Task complete! 🎉');
      onUpdate();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleQuerySubmit = async () => {
    if (!queryTitle.trim()) return toast.error('Please enter a title');
    if (!queryDesc.trim()) return toast.error('Please enter a description');
    setSavingQuery(true);
    try {
      await api.post('/queries', {
        task_id: task.id, project_id: task.project_id, department_id: task.department_id,
        query_type: queryType, title: queryTitle, description: queryDesc, priority: queryPriority
      });
      toast.success('Sent! ✅');
      setShowQueryForm(false);
      setQueryTitle(''); setQueryDesc('');
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
    finally { setSavingQuery(false); }
  };

  const TABS = [
    { id: 'progress', label: '✏️ Progress' },
    { id: 'reference', label: '🖼️ Reference' },
    { id: 'photos',   label: '📷 Photos'   },
    { id: 'query',    label: '📝 Query'    },
    { id: 'details',  label: 'ℹ️ Details'  },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <StatusBadge status={isDelayed && !isDone ? 'delayed' : task.status} />
                {task.stage_order > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">Stage {task.stage_order}</span>
                )}
              </div>
              <p className="font-bold text-gray-900 text-base leading-tight">{task.task_title}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">📁 {task.project_name}</span>
                {task.item_name && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">📦 {task.item_name}</span>}
                {task.department_name && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: (task.dept_color||'#888')+'25', color: task.dept_color||'#555' }}>
                    🏭 {task.department_name}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-xl hover:bg-gray-100 flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar in header */}
          {!isWaiting && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500">Progress</span>
                <span className="font-bold text-gray-800">{task.quantity_completed}/{task.quantity_assigned} ({pct}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full transition-all ${isDone ? 'bg-green-500' : task.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-400'}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          {/* Clock In/Out in header */}
          {!isDone && !isWaiting && (
            <div className="mt-3 flex items-center justify-between">
              <TimeTracker task={task} onSessionChange={setActiveSession} />
              {!isDone && (
                <button onClick={quickComplete} disabled={saving}
                  className="text-xs px-3 py-1.5 rounded-xl font-bold flex items-center gap-1 disabled:opacity-50 bg-green-500 hover:bg-green-600 text-white">
                  <CheckCircle className="w-3.5 h-3.5" /> Mark Done
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-xs font-semibold transition-all ${activeTab === tab.id ? 'text-amber-600 border-b-2 border-amber-500 bg-white' : 'text-gray-400 hover:text-gray-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">

          {/* ── PROGRESS TAB ── */}
          {activeTab === 'progress' && (
            <div className="space-y-4">
              {isWaiting && (
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-2xl p-4 text-sm text-gray-500">
                  <Clock className="w-5 h-5 flex-shrink-0 text-gray-400" />
                  Waiting for previous stage to complete...
                </div>
              )}

              {!isWaiting && (
                <>
                  {constraint?.has_constraint && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                      ℹ️ {constraint.message}
                    </div>
                  )}

                  {/* Qty input — big and easy to tap */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Qty Completed <span className="text-gray-400 font-normal text-xs">(Max: {maxQty})</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQty(q => Math.max(0, q - 1))}
                        className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-2xl text-xl font-bold text-gray-700 flex items-center justify-center flex-shrink-0">−</button>
                      <input type="number" min="0" max={maxQty} value={qty}
                        onChange={e => setQty(Math.min(maxQty, Math.max(0, parseInt(e.target.value)||0)))}
                        className="flex-1 border-2 border-gray-200 focus:border-amber-400 rounded-2xl px-3 py-3 text-2xl font-bold text-center focus:outline-none" />
                      <button onClick={() => setQty(q => Math.min(maxQty, q + 1))}
                        className="w-12 h-12 bg-gray-100 hover:bg-gray-200 rounded-2xl text-xl font-bold text-gray-700 flex items-center justify-center flex-shrink-0">+</button>
                    </div>
                    <div className="flex justify-between text-xs mt-2 px-1">
                      <span className="text-green-600">✓ Done: {task.quantity_completed}</span>
                      <span className="text-orange-500">Remaining: {task.quantity_assigned - task.quantity_completed}</span>
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[['pending','⏳ Pending'],['in_progress','🔵 In Progress'],['completed','✅ Completed'],['on_hold','⏸ On Hold']].map(([v,l]) => (
                        <button key={v} onClick={() => setStatus(v)}
                          className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${status === v ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Notes (optional)</label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                      placeholder="Any notes to add..."
                      className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2 text-sm focus:outline-none resize-none" />
                  </div>

                  {/* Save button */}
                  <button onClick={handleSave} disabled={saving}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3.5 rounded-2xl text-base font-bold disabled:opacity-50 transition-colors shadow-sm">
                    {saving ? 'Save ho raha...' : '💾 Save Progress'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* ── REFERENCE TAB ── */}
          {activeTab === 'reference' && (
            <ReferenceImages task={task} />
          )}

          {/* ── PHOTOS TAB ── */}
          {activeTab === 'photos' && (
            <ImageSection task={task} onUploaded={onUpdate} />
          )}

          {/* ── QUERY TAB ── */}
          {activeTab === 'query' && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-1 bg-gray-100 rounded-2xl p-1">
                {[['issue','🔧 Issue'],['material_request','📦 Material'],['clarification','❓ Ask'],['other','💬 Other']].map(([v,l]) => (
                  <button key={v} onClick={() => setQueryType(v)}
                    className={`py-2 rounded-xl text-xs font-bold transition-all ${queryType === v ? 'bg-white shadow text-amber-600' : 'text-gray-400'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5">
                {[['low','🟢'],['medium','🟡'],['high','🔴'],['urgent','🚨']].map(([v,l]) => (
                  <button key={v} onClick={() => setQueryPriority(v)}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${queryPriority === v ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500'}`}>
                    {l} {v}
                  </button>
                ))}
              </div>
              <input value={queryTitle} onChange={e => setQueryTitle(e.target.value)}
                placeholder="Briefly describe the problem..."
                className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
              <textarea value={queryDesc} onChange={e => setQueryDesc(e.target.value)} rows={3}
                placeholder="Describe in detail..."
                className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none resize-none" />
              <button onClick={handleQuerySubmit} disabled={savingQuery}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-2xl text-sm font-bold disabled:opacity-50">
                {savingQuery ? 'Sending...' : '📤 Send to Admin'}
              </button>
            </div>
          )}

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && (
            <div className="space-y-3">
              {task.admin_notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs font-bold text-amber-600 mb-1">📋 Admin Notes</p>
                  <p className="text-sm text-amber-900">{task.admin_notes}</p>
                </div>
              )}
              {task.worker_notes && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <p className="text-xs font-bold text-gray-500 mb-1">🗒️ Your Notes</p>
                  <p className="text-sm text-gray-700">{task.worker_notes}</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                {[
                  ['Project', task.project_name],
                  ['Item', task.item_name],
                  ['Department', task.department_name],
                  ['Stage', task.stage_order ? `Stage ${task.stage_order}` : '-'],
                  ['Assigned Qty', task.quantity_assigned],
                  ['Priority', task.priority || 'medium'],
                  ['Due Date', safeDate(task.due_date)],
                  ['Start Date', safeDate(task.start_date)],
                  task.completed_date && ['Completed On', safeDate(task.completed_date)],
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-semibold text-gray-800 text-right">{value || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Task Card (compact) ───────────────────────────────────────────────────────
function TaskCard({ task, onClick }) {
  const isDelayed = task.status !== 'completed' && task.due_date && task.due_date < today();
  const isDone = task.status === 'completed';
  const isWaiting = task.status === 'waiting';
  const pct = task.quantity_assigned > 0 ? Math.round((task.quantity_completed / task.quantity_assigned) * 100) : 0;

  const borderColor = isDone ? 'border-l-green-400' : isWaiting ? 'border-l-gray-300' : isDelayed ? 'border-l-red-400' : task.status === 'in_progress' ? 'border-l-blue-400' : 'border-l-amber-400';

  return (
    <div onClick={onClick}
      className={`bg-white rounded-2xl border border-gray-100 border-l-4 ${borderColor} shadow-sm p-4 cursor-pointer hover:shadow-md active:scale-[0.98] transition-all`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <StatusBadge status={isDelayed && !isDone ? 'delayed' : task.status} />
            {task.stage_order > 0 && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">S{task.stage_order}</span>
            )}
          </div>
          <p className="font-bold text-gray-900 text-sm leading-snug truncate">{task.task_title}</p>
          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400 flex-wrap">
            {task.item_name && <span>📦 {task.item_name}</span>}
            {task.proto_code && <span className="font-mono bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-bold">📷 {task.proto_code}</span>}
            {task.department_name && <span style={{ color: task.dept_color }}>🏭 {task.department_name}</span>}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-400 flex-wrap">
            <span>📁 {task.proj_code} {task.project_name && <span className="text-gray-400">— {task.project_name}</span>}</span>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-lg font-bold text-gray-800">{pct}%</p>
          <p className="text-xs text-gray-400">{task.quantity_completed}/{task.quantity_assigned}</p>
        </div>
      </div>

      {/* Mini progress bar */}
      {!isWaiting && (
        <div className="mt-3 w-full bg-gray-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full transition-all ${isDone ? 'bg-green-500' : task.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-400'}`}
            style={{ width: `${pct}%` }} />
        </div>
      )}

      {isWaiting && (
        <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
          <Clock className="w-3 h-3" /> Waiting for previous stage...
        </div>
      )}
    </div>
  );
}

// ── Stats Row ─────────────────────────────────────────────────────────────────
function StatsRow({ tasks, activeFilter, onFilterChange }) {
  const counts = {
    all:         tasks.length,
    pending:     tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    waiting:     tasks.filter(t => t.status === 'waiting').length,
    completed:   tasks.filter(t => t.status === 'completed').length,
  };

  const stats = [
    { key: 'pending',     label: 'Pending',    emoji: '⏳', color: 'text-yellow-600', bg: 'bg-yellow-50',  activeBg: 'bg-yellow-500'  },
    { key: 'in_progress', label: 'Active',     emoji: '🔵', color: 'text-blue-600',   bg: 'bg-blue-50',    activeBg: 'bg-blue-500'    },
    { key: 'waiting',     label: 'Waiting',    emoji: '⏸', color: 'text-gray-500',   bg: 'bg-gray-50',    activeBg: 'bg-gray-400'    },
    { key: 'completed',   label: 'Done',       emoji: '✅', color: 'text-green-600',  bg: 'bg-green-50',   activeBg: 'bg-green-500'   },
  ];

  return (
    <div className="grid grid-cols-4 gap-2">
      {stats.map(s => {
        const isActive = activeFilter === s.key;
        return (
          <button key={s.key} onClick={() => onFilterChange(isActive ? '' : s.key)}
            className={`rounded-2xl p-2.5 text-center transition-all ${isActive ? `${s.activeBg} text-white shadow-md` : `${s.bg} ${s.color}`}`}>
            <p className={`text-xl font-bold ${isActive ? 'text-white' : ''}`}>{counts[s.key]}</p>
            <p className={`text-xs font-semibold mt-0.5 ${isActive ? 'text-white/90' : ''}`}>{s.label}</p>
          </button>
        );
      })}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function WorkerTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (soft = false) => {
    if (soft) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get('/tasks/my', { params: { status: 'all' } });
      setTasks(r.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredTasks = tasks.filter(t => {
    // Status filter
    const statusOk = statusFilter
      ? t.status === statusFilter
      : ['pending', 'in_progress', 'waiting'].includes(t.status);

    // Search filter — proto_code, item_name, task_title, project_name
    const q = search.trim().toLowerCase();
    const searchOk = !q || [
      t.proto_code, t.item_name, t.task_title, t.project_name, t.proj_code
    ].some(v => v && v.toLowerCase().includes(q));

    return statusOk && searchOk;
  });

  // Group by project
  const grouped = filteredTasks.reduce((acc, t) => {
    if (!acc[t.project_id]) acc[t.project_id] = { name: t.project_name, code: t.proj_code, proto_code: t.proto_code || '', tasks: [] };
    acc[t.project_id].tasks.push(t);
    return acc;
  }, {});

  const uniqueProjects = Object.keys(grouped).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-3 py-4 space-y-4 pb-10">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-xs text-gray-400 mt-0.5">{tasks.length} total</p>
          </div>
          <button onClick={() => load(true)} disabled={refreshing}
            className="flex items-center gap-1.5 text-sm border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats — clickable filters */}
        {tasks.length > 0 && (
          <StatsRow tasks={tasks} activeFilter={statusFilter} onFilterChange={setStatusFilter} />
        )}

        {/* Search Bar */}
        <div className="relative">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search: proto code, item name, project..."
            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 pr-8"
          />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Active filter label */}
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {search ? `🔍 "${search}"` :
             statusFilter === 'pending' ? '⏳ Pending Tasks' :
             statusFilter === 'in_progress' ? '🔵 In Progress Tasks' :
             statusFilter === 'waiting' ? '⏸ Waiting Tasks' :
             statusFilter === 'completed' ? '✅ Completed Tasks' :
             '🔥 Active Tasks'}
            <span className="text-gray-400 font-normal ml-1">({filteredTasks.length})</span>
          </p>
          {(statusFilter || search) && (
            <button onClick={() => { setStatusFilter(''); setSearch(''); }}
              className="text-xs text-amber-600 font-semibold">
              Clear ✕
            </button>
          )}
        </div>

        {/* Task List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            <p className="text-sm text-gray-400">Loading...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="font-bold text-gray-400">No tasks found</p>
            <p className="text-xs text-gray-300 mt-1">Change filter or refresh</p>
          </div>
        ) : (
          Object.entries(grouped).map(([projectId, group]) => (
            <div key={projectId} className="space-y-2">
              {uniqueProjects > 1 && (
                <div className="flex items-center gap-2 px-1 my-1">
                  <div className="h-px flex-1 bg-gray-200" />
                  <div className="flex flex-col items-center">
                    <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full whitespace-nowrap">
                      📁 {group.code}
                    </span>
                    {group.name && <span className="text-xs text-gray-500 mt-0.5 font-medium">{group.name}</span>}
                    {group.proto_code && <span className="text-xs font-mono text-amber-600 mt-0.5 font-bold">📷 {group.proto_code}</span>}
                  </div>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
              )}
              {group.tasks.map(task => (
                <TaskCard key={task.id} task={task} onClick={() => setSelectedTask(task)} />
              ))}
            </div>
          ))
        )}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={() => {
            load(true);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
}
